import { useState, useEffect } from "react";
import { doc, updateDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES, STAFF, STAFF_COLORS } from "../../config/constants";
import { db } from "../../firebase";
import { staffAvatar, btnP, btnS } from "../../utils/styles";

/* ── Auto-link URLs in text ── */
function Linkify({ text, color }) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s<>"']+)/gi;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      urlRegex.lastIndex = 0; // reset regex state
      let label = part;
      try {
        const u = new URL(part);
        label = u.hostname.replace("www.", "") + (u.pathname.length > 1 ? u.pathname.substring(0, 20) + (u.pathname.length > 20 ? "..." : "") : "");
      } catch(e) { /* keep full url */ }
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: BRAND.blue, textDecoration: "underline", wordBreak: "break-all" }}>{label}</a>;
    }
    return <span key={i}>{part}</span>;
  });
}

export default function TaskModal({ task, currentUser, onClose, theme, clients, onNavigateToClient }) {
  const t = THEMES[theme];
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const [reassignNote, setReassignNote] = useState("");
  const [spUrl, setSpUrl] = useState(task.oneDriveUrl || "");

  // Look up SharePoint URL from onedrive_links if not stored on task
  useEffect(() => {
    if (task.oneDriveUrl) { setSpUrl(task.oneDriveUrl); return; }
    if (!task.clientId) return;
    getDocs(collection(db, "onedrive_links")).then(snap => {
      const match = snap.docs.find(d => d.data().clientId === task.clientId);
      if (match) {
        const data = match.data();
        const url = data.webUrl || data.sharePointUrl || "";
        if (url) setSpUrl(url);
      }
    }).catch(() => {});
  }, [task.clientId, task.oneDriveUrl]);

  const isAssignedToMe = task.assignedTo === currentUser;
  const isCreatedByMe  = task.createdBy === currentUser;
  const isSelfAssigned = task.createdBy === task.assignedTo;

  /* ── actions ── */
  const addComment = async (action) => {
    if (!comment.trim() && action === "comment") return;
    setSaving(true);
    try {
      const comments = [...(task.comments || [])];
      if (comment.trim()) {
        comments.push({ from: currentUser, text: comment.trim(), createdAt: new Date().toISOString(), type: "comment" });
      }
      const updates = { updatedAt: serverTimestamp(), comments };
      if (action === "close") {
        updates.status = "closed";
        comments.push({ from: currentUser, text: "Closed this task", createdAt: new Date().toISOString(), type: "system" });
        updates.comments = comments;
      }
      if (action === "resolve") {
        updates.status = "resolved";
        comments.push({ from: currentUser, text: "Resolved this task", createdAt: new Date().toISOString(), type: "system" });
        updates.comments = comments;
      }
      await updateDoc(doc(db, "tasks", task.id), updates);
      setComment("");
      if (action === "close") onClose();
    } catch (e) { alert("Failed: " + e.message); }
    setSaving(false);
  };

  const handleReopen = async () => {
    setSaving(true);
    try {
      const comments = [...(task.comments || [])];
      comments.push({ from: currentUser, text: "Reopened this task", createdAt: new Date().toISOString(), type: "system" });
      await updateDoc(doc(db, "tasks", task.id), { status: "open", comments, updatedAt: serverTimestamp() });
    } catch (e) { alert("Failed: " + e.message); }
    setSaving(false);
  };

  const handleReassign = async () => {
    if (!reassignTo) return;
    setSaving(true);
    try {
      const comments = [...(task.comments || [])];
      if (reassignNote.trim()) {
        comments.push({ from: currentUser, text: reassignNote.trim(), createdAt: new Date().toISOString(), type: "comment" });
      }
      comments.push({ from: currentUser, text: "Reassigned task from " + task.assignedTo + " → " + reassignTo, createdAt: new Date().toISOString(), type: "system" });
      await updateDoc(doc(db, "tasks", task.id), { assignedTo: reassignTo, status: "open", comments, updatedAt: serverTimestamp() });
      setShowReassign(false); setReassignTo(""); setReassignNote("");
    } catch (e) { alert("Failed: " + e.message); }
    setSaving(false);
  };

  const handleMarkDone = async () => {
    setSaving(true);
    try {
      const comments = [...(task.comments || [])];
      if (comment.trim()) {
        comments.push({ from: currentUser, text: comment.trim(), createdAt: new Date().toISOString(), type: "comment" });
      }
      comments.push({ from: currentUser, text: "Marked task as done", createdAt: new Date().toISOString(), type: "system" });
      if (isSelfAssigned) {
        await updateDoc(doc(db, "tasks", task.id), { status: "closed", comments, updatedAt: serverTimestamp() });
        setComment(""); onClose();
      } else {
        await updateDoc(doc(db, "tasks", task.id), { assignedTo: task.createdBy, status: "completed", comments, updatedAt: serverTimestamp() });
        setComment("");
      }
    } catch (e) { alert("Failed: " + e.message); }
    setSaving(false);
  };

  const handleAcceptClose = async () => {
    setSaving(true);
    try {
      const comments = [...(task.comments || [])];
      comments.push({ from: currentUser, text: "Accepted & closed this task", createdAt: new Date().toISOString(), type: "system" });
      await updateDoc(doc(db, "tasks", task.id), { status: "closed", comments, updatedAt: serverTimestamp() });
      onClose();
    } catch (e) { alert("Failed: " + e.message); }
    setSaving(false);
  };

  const statusMap = { open: { label: "OPEN", color: "#f59e0b" }, completed: { label: "COMPLETED", color: BRAND.green }, resolved: { label: "RESOLVED", color: BRAND.blue }, closed: { label: "CLOSED", color: "#6b7280" } };
  const sl = statusMap[task.status] || { label: (task.status || "").toUpperCase(), color: t.textMuted };
  const inp = { background: t.inputBg || t.bg, border: "1px solid " + t.border, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", fontFamily: "'Outfit',sans-serif", width: "100%", boxSizing: "border-box" };

  const timeAgo = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const diff = Date.now() - d;
    if (diff < 60000) return "just now";
    if (diff < 3600000) return Math.floor(diff / 60000) + "m ago";
    if (diff < 86400000) return Math.floor(diff / 3600000) + "h ago";
    return d.toLocaleDateString();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: t.surface, borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>

        {/* Header */}
        <div style={{ padding: "20px 28px 16px", borderBottom: "1px solid " + t.border, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: sl.color + "20", color: sl.color, border: "1px solid " + sl.color + "40" }}>{sl.label}</span>
              {task.clientName && <span onClick={()=>{if(onNavigateToClient&&task.clientId){const c=clients?.find(cl=>cl.id===task.clientId);if(c){onClose();onNavigateToClient(c);}}}} style={{ fontSize: 11, color: onNavigateToClient&&task.clientId?BRAND.blue:t.textMuted, background: t.bg, padding: "2px 8px", borderRadius: 6, cursor: onNavigateToClient&&task.clientId?"pointer":"default" }}>👤 {task.clientName}</span>}
              {task.businessName && <span style={{ fontSize: 11, color: BRAND.green, background: BRAND.green + "12", padding: "2px 8px", borderRadius: 6 }}>🏢 {task.businessName}</span>}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: t.text, lineHeight: 1.3, marginBottom: 8 }}>{task.title}</div>
            <div style={{ fontSize: 12.5, color: t.textMuted, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
              {staffAvatar(task.createdBy, 16)}
              <span>Created by <strong style={{ color: STAFF_COLORS[task.createdBy] || t.text }}>{task.createdBy}</strong></span>
              <span style={{ color: t.border }}>→</span>
              {staffAvatar(task.assignedTo, 16)}
              <span>Assigned to <strong style={{ color: STAFF_COLORS[task.assignedTo] || t.text }}>{task.assignedTo}</strong></span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, marginLeft: 8, flexShrink: 0 }}><Icon name="x" size={20} color={t.textSub} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px" }}>
          {/* Description */}
          {task.description && (
            <div style={{ background: t.bg, border: "1px solid " + t.border, borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Description</div>
              <div style={{ fontSize: 13.5, color: t.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}><Linkify text={task.description} /></div>
            </div>
          )}

          {/* SharePoint folder link */}
          {spUrl && (
            <a href={spUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderRadius: 10, background: "#0078d410", border: "1px solid #0078d430", textDecoration: "none", marginBottom: 18, transition: "all 0.12s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#0078d420"}
              onMouseLeave={e => e.currentTarget.style.background = "#0078d410"}>
              <span style={{ fontSize: 22 }}>📁</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0078d4" }}>Open Client SharePoint Folder</div>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 1 }}>View documents and files for {task.clientName || "this client"}</div>
              </div>
              <span style={{ marginLeft: "auto", fontSize: 14, color: "#0078d4" }}>↗</span>
            </a>
          )}

          {/* Activity */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>Activity</div>
            {(task.comments || []).length === 0 ? (
              <div style={{ fontSize: 13, color: t.textMuted, fontStyle: "italic", padding: "8px 0" }}>No activity yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {(task.comments || []).map((c, i) => (
                  c.type === "system" ? (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", paddingLeft: 38 }}>
                      <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                        {staffAvatar(c.from, 16)}
                        <span>{c.text}</span>
                        <span style={{ fontSize: 11, color: t.border, marginLeft: "auto", flexShrink: 0 }}>{timeAgo(c.createdAt)}</span>
                      </div>
                    </div>
                  ) : (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      {staffAvatar(c.from, 30)}
                      <div style={{ flex: 1, background: c.from === currentUser ? (BRAND.blue + "0a") : t.bg, border: "1px solid " + t.border, borderRadius: 10, padding: "12px 16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: STAFF_COLORS[c.from] || t.text }}>{c.from}</span>
                          <span style={{ fontSize: 11, color: t.textMuted }}>{timeAgo(c.createdAt)}</span>
                        </div>
                        <div style={{ fontSize: 13.5, color: t.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}><Linkify text={c.text} /></div>
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>

          {/* Reassign panel */}
          {showReassign && task.status !== "closed" && (
            <div style={{ background: BRAND.blue + "08", border: "1px solid " + BRAND.blue + "30", borderRadius: 12, padding: 18, marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 12 }}>Reassign Task</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                {STAFF.filter(s => s !== task.assignedTo).map(s => (
                  <button key={s} type="button" onClick={() => setReassignTo(s)}
                    style={{ padding: "9px 18px", borderRadius: 8, border: "2px solid " + (reassignTo === s ? STAFF_COLORS[s] : t.border), background: reassignTo === s ? STAFF_COLORS[s] + "18" : "transparent", color: reassignTo === s ? STAFF_COLORS[s] : t.textMuted, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 7 }}>
                    {staffAvatar(s, 20)}{s}
                  </button>
                ))}
              </div>
              <textarea value={reassignNote} onChange={e => setReassignNote(e.target.value)}
                placeholder="Add a note for the next person (optional)..."
                style={{ ...inp, minHeight: 70, resize: "vertical", marginBottom: 12 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleReassign} disabled={!reassignTo || saving}
                  style={{ ...btnP, padding: "9px 20px", fontSize: 13, opacity: !reassignTo ? 0.5 : 1 }}>
                  {saving ? <MiniSpinner size={13} color="#fff" /> : "↗ Reassign to " + (reassignTo || "...")}
                </button>
                <button onClick={() => { setShowReassign(false); setReassignTo(""); setReassignNote(""); }}
                  style={{ padding: "9px 16px", borderRadius: 8, background: "transparent", border: "1px solid " + t.border, color: t.textMuted, fontSize: 13, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Comment box */}
          {task.status !== "closed" && !showReassign && (
            <textarea value={comment} onChange={e => setComment(e.target.value)}
              placeholder="Add a comment..."
              style={{ ...inp, minHeight: 100, resize: "vertical", fontSize: 13.5 }} />
          )}
        </div>

        {/* Footer */}
        {(task.status === "open" || task.status === "completed") && !showReassign && (
          <div style={{ padding: "16px 28px", borderTop: "1px solid " + t.border, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            {comment.trim() && (
              <button onClick={() => addComment("comment")} disabled={saving}
                style={{ ...btnS(t), padding: "9px 18px", fontSize: 13 }}>
                {saving ? <MiniSpinner size={13} color={BRAND.blue} /> : "💬 Comment"}
              </button>
            )}
            {isAssignedToMe && task.status === "open" && (
              <button onClick={handleMarkDone} disabled={saving}
                style={{ ...btnP, padding: "9px 20px", fontSize: 13, background: "linear-gradient(135deg," + BRAND.green + ",#5a9a20)" }}>
                {saving ? <MiniSpinner size={13} color="#fff" /> : isSelfAssigned ? "✓ Mark Done" : "✓ Done — Return to " + task.createdBy}
              </button>
            )}
            {isAssignedToMe && task.status === "completed" && (
              <>
                <button onClick={handleAcceptClose} disabled={saving}
                  style={{ ...btnP, padding: "9px 20px", fontSize: 13, background: "linear-gradient(135deg," + BRAND.green + ",#5a9a20)" }}>
                  {saving ? <MiniSpinner size={13} color="#fff" /> : "✓ Accept & Close"}
                </button>
                <button onClick={() => setShowReassign(true)}
                  style={{ ...btnS(t), padding: "9px 18px", fontSize: 13 }}>
                  ↗ Reassign Instead
                </button>
              </>
            )}
            {task.status === "open" && (
              <button onClick={() => setShowReassign(true)}
                style={{ ...btnS(t), padding: "9px 18px", fontSize: 13 }}>
                ↗ Reassign
              </button>
            )}
            {(isCreatedByMe || isAssignedToMe) && (
              <button onClick={() => addComment("resolve")} disabled={saving}
                style={{ padding: "9px 18px", borderRadius: 8, background: "transparent", border: "1px solid " + BRAND.blue + "40", color: BRAND.blue, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif", marginLeft: "auto" }}>
                {saving ? <MiniSpinner size={13} color={BRAND.blue} /> : "✓ Resolve"}
              </button>
            )}
          </div>
        )}
        {task.status === "resolved" && (
          <div style={{ padding: "16px 28px", borderTop: "1px solid " + t.border, display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 13, color: BRAND.blue, fontWeight: 600 }}>✓ Task resolved</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleReopen} disabled={saving}
                style={{ ...btnS(t), padding: "9px 18px", fontSize: 13 }}>
                {saving ? <MiniSpinner size={13} color={BRAND.blue} /> : "↩ Reopen"}
              </button>
              <button onClick={() => addComment("close")} disabled={saving}
                style={{ padding: "9px 18px", borderRadius: 8, background: "transparent", border: "1px solid " + t.border, color: t.textMuted, fontSize: 13, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
                {saving ? <MiniSpinner size={13} color={t.textMuted} /> : "Archive"}
              </button>
            </div>
          </div>
        )}
        {task.status === "closed" && (
          <div style={{ padding: "16px 28px", borderTop: "1px solid " + t.border, display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 600 }}>Archived</div>
            <button onClick={handleReopen} disabled={saving}
              style={{ ...btnS(t), padding: "9px 18px", fontSize: 13 }}>
              {saving ? <MiniSpinner size={13} color={BRAND.blue} /> : "↩ Reopen"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
